/**
 * Scene Format Plugin Registry
 *
 * Provides a registry of scene format plugins for direct instantiation.
 * Used by TraceScene for loading scene files without going through
 * Babylon's global plugin registration.
 *
 * This is separate from registerPlugins.ts which registers plugins
 * globally with Babylon.js for use with AppendSceneAsync/ImportMeshAsync.
 */

import type { Scene } from "@babylonjs/core/scene";
import type { LoadedSceneResult } from './LoadedSceneResult';

import { SCENE_FORMAT_EXTENSIONS } from './sceneFormatExtensions';

import { TraceSceneLoaderPlugin } from './TraceSceneLoaderPlugin';
import { BundleSceneLoaderPlugin } from './BundleSceneLoaderPlugin';
import { GltfSceneLoaderPlugin } from './GltfSceneLoaderPlugin';
import { AeroSceneLoaderPlugin } from './AeroSceneLoaderPlugin';
import { TraceZipSceneLoaderPlugin } from './TraceZipSceneLoaderPlugin';

// =============================================================================
// Types
// =============================================================================

/**
 * Interface for scene loader plugins that support loadWithResult.
 * This is the common interface used by TraceScene for direct plugin loading.
 */
export interface ITraceSceneLoaderPlugin {
    loadWithResult(
        scene: Scene,
        data: unknown,
        rootUrl: string,
        onProgress?: (loaded: number, total: number) => void,
        /** The file the bytes came from — names a synthesized document root (§3). */
        fileName?: string,
    ): Promise<LoadedSceneResult>;
}

/**
 * Configuration for a scene format plugin
 */
export interface SceneFormatConfig<T extends ITraceSceneLoaderPlugin = ITraceSceneLoaderPlugin> {
    /** Factory function to create the plugin instance */
    createPlugin: () => T;
    /** How to read the blob data */
    dataType: 'arraybuffer' | 'text';
    /** Whether this format needs a rootUrl for resolving relative asset paths */
    needsRootUrl?: boolean;
}

// =============================================================================
// Registry
// =============================================================================

/**
 * Get the configuration for a scene format by file extension.
 *
 * Extensions that unambiguously name a scene format only. `.gltf`/`.glb` are
 * NOT here — they need a content sniff (see {@link getSceneDocumentFormatConfig}).
 *
 * @param extension - File extension without the dot (e.g., 'trace', 'glz', 'glxf')
 * @returns The format configuration, or undefined if not supported
 *
 * @example
 * ```typescript
 * const config = getSceneFormatConfig('glz');
 * if (config) {
 *     const plugin = config.createPlugin();
 *     const data = await blob.arrayBuffer();
 *     const result = await plugin.loadWithResult(scene, data, rootUrl);
 * }
 * ```
 */
export function getSceneFormatConfig(extension: string): SceneFormatConfig | undefined {
    switch (extension) {
        case 'trace':
            return {
                createPlugin: () => new TraceSceneLoaderPlugin(),
                dataType: 'arraybuffer',
            };
        case 'glz':
        // `.tracex` is the same container under its retired name (spec §5).
        case 'tracex':
            return {
                createPlugin: () => new BundleSceneLoaderPlugin(),
                dataType: 'arraybuffer',
            };
        case 'zip':
            return {
                createPlugin: () => new TraceZipSceneLoaderPlugin(),
                dataType: 'arraybuffer',
            };
        case 'real':
            return {
                createPlugin: () => new AeroSceneLoaderPlugin(),
                dataType: 'arraybuffer',
            };
        case 'glxf':
            // Legacy alias: a glXF document reads through the same normalizer.
            return getSceneDocumentFormatConfig();
        default:
            return undefined;
    }
}

/**
 * Configuration for the **bundle** reader (`.glz`, spec §4), for a caller that
 * sniffed the BYTES rather than the extension — a zip carrying `scene.gltf` at
 * its root is a bundle whatever it is called (`TraceScene._loadFromBlob`).
 */
export function getBundleFormatConfig(): SceneFormatConfig {
    return {
        createPlugin: () => new BundleSceneLoaderPlugin(),
        dataType: 'arraybuffer',
    };
}

/**
 * Configuration for a glTF 2.1 **scene document** (`.gltf` / `.glb`).
 *
 * Deliberately not reachable from {@link getSceneFormatConfig}: those
 * extensions name plain models just as often, so nothing may route to this
 * plugin on the extension alone. Callers sniff the content first — see
 * `isSceneDocument` in `loaders/formats/gltf21/normalize.ts` and
 * `TraceViewer.open` (migration plan D2).
 */
export function getSceneDocumentFormatConfig(): SceneFormatConfig {
    return {
        createPlugin: () => new GltfSceneLoaderPlugin(),
        dataType: 'text',
        needsRootUrl: true,
    };
}

/**
 * Get list of all supported scene format extensions.
 */
export function getSupportedSceneFormats(): string[] {
    return [...SCENE_FORMAT_EXTENSIONS];
}

// Extension predicates live in a leaf module so plugins can import them
// without a registry→plugin→registry cycle; re-exported here to keep the
// existing import surface.
export { isSceneFormat, isSceneFormatRef, isGltfExtension, isLegacySceneFormat } from './sceneFormatExtensions';
